"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.propTypes = exports.defaultProps = exports.default = exports.BannerJumbo = void 0;
require("./BannerJumbo.css");
var _classnames = _interopRequireDefault(require("classnames"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _react = _interopRequireDefault(require("react"));
var _Card = _interopRequireDefault(require("../Card/Card"));
var _LoaderGhostCard = _interopRequireDefault(require("../LoaderGhostCard/LoaderGhostCard"));
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
/**
 * BannerJumbo component
 *
 * @method
 * @param { BannerJumboProps } props
 * @returns BannerJumbo
 */

const BannerJumbo = props => {
  const {
    actionGroups,
    backgroundColor,
    bannerImage,
    body,
    body2,
    body3,
    borderColor = defaultProps.borderColor,
    bulletedList,
    cornerRadius = defaultProps.cornerRadius,
    elevation = defaultProps.elevation,
    externalActionElement,
    externalImageElement,
    externalFullyClickableActionElement,
    fullyClickableParent,
    eyebrow,
    headline,
    headline2,
    headline3,
    headlineTag,
    isLoading = defaultProps.isLoading,
    loaderHeightLg,
    loaderHeightMd,
    loaderHeightSm,
    loaderHeightXl,
    tag,
    imagePosition = defaultProps.imagePosition,
    textColor = defaultProps.textColor
  } = props;
  if (isLoading) {
    return /*#__PURE__*/_react.default.createElement("div", {
      className: "pal-c-BannerJumbo"
    }, /*#__PURE__*/_react.default.createElement(_LoaderGhostCard.default, {
      className: "pal-c-BannerJumbo__Loader",
      style: {
        '--pal-loaderghost-card-height-sm': loaderHeightSm,
        '--pal-loaderghost-card-height-md': loaderHeightMd,
        '--pal-loaderghost-card-height-lg': loaderHeightLg,
        '--pal-loaderghost-card-height-xl': loaderHeightXl
      }
    }));
  }
  const componentClassName = (0, _classnames.default)('pal-c-BannerJumbo', {
    [`pal-c-BannerJumbo__elevation--${elevation}`]: elevation,
    [`pal-c-BannerJumbo--withPadding`]: backgroundColor || borderColor || elevation,
    'pal-c-BannerJumbo__corners--rounded': cornerRadius,
    [`pal-c-BannerJumbo--withBorder`]: borderColor,
    [`pal-c-BannerJumbo__imagePosition--${imagePosition}`]: imagePosition
  });
  const componentStyle = {
    ...(borderColor && {
      '--borderColor': `var( --pal-colors-default-${borderColor} )`
    }),
    ...(textColor && {
      '--textColor': `var( --pal-colors-default-${textColor} )`
    }),
    ...(cornerRadius && {
      '--cornerRadius': `var( --pal-${cornerRadius} )`
    })
  };
  return /*#__PURE__*/_react.default.createElement("div", {
    className: componentClassName,
    style: componentStyle
  }, /*#__PURE__*/_react.default.createElement(_Card.default, {
    actionGroups: actionGroups,
    backgroundColor: backgroundColor,
    externalMedia: externalImageElement,
    image: bannerImage,
    imgGap: 'md',
    imgAlign: imagePosition,
    contentGap: "lg",
    contentAlign: "middle",
    bulletedList: bulletedList,
    tag: tag,
    eyebrow: eyebrow,
    externalActionFullyClick: externalFullyClickableActionElement,
    fullyClickableParent: fullyClickableParent,
    title: {
      text: headline,
      tagName: headlineTag,
      textStyle: 'title-xlg'
    },
    description: {
      text: body,
      textStyle: 'body-xlg'
    },
    subTitleOne: {
      text: headline2,
      textStyle: 'title'
    },
    subDescriptionOne: {
      text: body2,
      textStyle: 'body-lg'
    },
    subTitleTwo: {
      text: headline3,
      textStyle: 'title-lg'
    },
    subDescriptionTwo: {
      text: body3,
      textStyle: 'body-lg'
    },
    externalAction: externalActionElement
  }));
};

/**
 * Property type definitions
 * @typedef BannerJumboProps
 * @type {object}
 * @property {object} actionGroups - Allows passing an object to the actionGroup prop
 * @property {string} backgroundColor - Sets the background color for the banner.
 * @property {object} bannerImage - Allows passing image object to render Palette Image component for the banner.
 * @property {string} body - Sets the body text for the banner
 * @property {string} body2 - Sets the body2 text for the banner
 * @property {string} body3 - Sets the body3 text for the banner
 * @property {string} borderColor - Sets the border color for the banner.
 * @property {array} bulletedList - Array of bulleted list items
 * @property {string} cornerRadius - Sets the border radius of the corner radius.
 * @property {string} elevation - Sets the elevation for the banner
 * @property {object} externalActionElement - Accepts actions to render from web-site with business logic.
 * @property {object} externalImageElement - Accepts UltaAsset to render the image for the banner.
 * @property {object} externalFullyClickableActionElement - Accepts fully clickable absolute action from web-site.
 * @property {string} eyebrow - Sets the eyebrow text
 * @property {boolean} fullyClickableParent - Sets the banner with one action to be fully clickable
 * @property {string} headline - Sets the headline text for the banner
 * @property {string} headline2 - Sets the headline2 text for the banner
 * @property {string} headline3 - Sets the headline3 text for the banner
 * @property {string} headlineTag - Accepts tag name to render headline text.
 * @property {boolean} isLoading - To show the loading state of the component.
 * @property {string} loaderHeightLg - The height of the loader on LG inflections (ex - "11rem")
 * @property {string} loaderHeightMd - The height of the loader on MD inflections (ex - "11rem")
 * @property {string} loaderHeightSm - The height of the loader on SM inflections (ex - "11rem")
 * @property {string} loaderHeightXl - The height of the loader on XL inflections (ex - "11rem")
 * @property {object} tag - Accepts object with properties for Tag component above the eyebrow
 * @property {string} imagePosition - Sets the image position for the banner. For small and medium viewport, content is aligned below the image.
 * @property {string} textColor - Sets the text color for the banner( content-default, content-knockout )
 */
exports.BannerJumbo = BannerJumbo;
const propTypes = exports.propTypes = {
  /**
   * Allows passing an object to the actionGroup prop
   */
  actionGroups: _propTypes.default.object,
  /**
   * Accepts background color for the banner.
   */
  backgroundColor: _propTypes.default.string,
  /**
   * Allows passing image object to render Palette Image component for the banner.
   */
  bannerImage: _propTypes.default.object,
  /**
   * Sets the body text for the banner
   */
  body: _propTypes.default.string,
  /**
   * Sets the body2 text for the banner
   */
  body2: _propTypes.default.string,
  /**
   * Sets the body3 text for the banner
   */
  body3: _propTypes.default.string,
  /**
   * Sets the border color for the banner
   */
  borderColor: _propTypes.default.string,
  /**
   * Array of bulleted list items
   */
  bulletedList: _propTypes.default.array,
  /**
   * Sets the border radius of the banner container
   */
  cornerRadius: _propTypes.default.string,
  /**
   * Sets the elevation for the banner
   */
  elevation: _propTypes.default.oneOf(['low', 'medium', 'high', 'top']),
  /**
   * Accepts actions to render from web-site with business logic.
   */
  externalActionElement: _propTypes.default.object,
  /**
   * Accepts UltaAsset to render the image for the banner.
   */
  externalImageElement: _propTypes.default.object,
  /**
   * Accepts fully clickable absolute action from web-site.
   */
  externalFullyClickableActionElement: _propTypes.default.object,
  /**
   * Sets the eyebrow text
   */
  eyebrow: _propTypes.default.string,
  /**
   * Sets the banner with one action to be fully clickable
   */
  fullyClickableParent: _propTypes.default.bool,
  /**
   * Sets the headline text for the banner
   */
  headline: _propTypes.default.string,
  /**
   * Sets the headline2 text for the banner
   */
  headline2: _propTypes.default.string,
  /**
   * Sets the headline3 text for the banner
   */
  headline3: _propTypes.default.string,
  /**
   * Accepts tag name to render headline text.
   */
  headlineTag: _propTypes.default.string,
  /**
   * To show the loading state of the component.
   */
  isLoading: _propTypes.default.bool,
  /**
   * The height of the loader on LG inflections (ex - "11rem")
   */
  loaderHeightLg: _propTypes.default.string,
  /**
   * The height of the loader on MD inflections (ex - "11rem")
   */
  loaderHeightMd: _propTypes.default.string,
  /**
   * The height of the loader on SM inflections (ex - "11rem")
   */
  loaderHeightSm: _propTypes.default.string,
  /**
   * The height of the loader on XL inflections (ex - "11rem")
   */
  loaderHeightXl: _propTypes.default.string,
  /**
   * Accepts object with properties for Tag component above the eyebrow
   */
  tag: _propTypes.default.object,
  /**
   * Sets the image position for the banner.
   * - For small and medium viewport, content is aligned below the image.
   */
  imagePosition: _propTypes.default.oneOf(['left', 'right']),
  /**
   * Sets the text color for the banner
   */
  textColor: _propTypes.default.oneOf(['content-default', 'content-knockout'])
};

/**
 * Default values for passed properties
 *
 * @type {object}
 * @property {string} borderColor=undefined - Set default borderColor of the banner to undefined
 * @property {string} cornerRadius=undefined - Set default cornerRadius to undefined
 * @property {string} elevation=undefined - Set default elevation of the banner to undefined
 * @property {string} imagePosition='right' - Set default imagePosition of the banner to right
 * @property {string} textColor='content-default' - Set default textColor of the banner to content-default
 * @property {boolean} isLoading=false - Set default isLoading to false
 */
const defaultProps = exports.defaultProps = {
  borderColor: undefined,
  cornerRadius: undefined,
  elevation: undefined,
  imagePosition: 'right',
  textColor: 'content-default',
  isLoading: false
};
BannerJumbo.propTypes = propTypes;
var _default = exports.default = BannerJumbo;